import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Modal, // Added Modal
  ModalHeader, // Added ModalHeader
  ModalBody, // Added ModalBody
  ModalFooter, // Added ModalFooter
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

import { fetchHistoricalOrders } from "actions/orderActions";
import { API } from "config"; // Assuming API base URL is in config

function OrderHistory() {
  const dispatch = useDispatch();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const historicalOrdersState = useSelector((state) => state.historicalOrders);
  const { loading, historicalOrders, error } = historicalOrdersState;

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false); // State for modal visibility
  const [selectedOrder, setSelectedOrder] = useState(null); // State for selected order details

  useEffect(() => {
    if (userInfo && userInfo.user && userInfo.user.erp_user) {
      const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
      const formattedEndDate = moment(endDate).format("YYYY-MM-DD");
      dispatch(
        fetchHistoricalOrders(
          formattedStartDate,
          formattedEndDate,
          userInfo.user.erp_user.outlet
        )
      );
    }
  }, [dispatch, userInfo, startDate, endDate]);

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const toggleModal = () => setShowModal(!showModal); // Function to toggle modal

  const handleRowClick = (order) => {
    setSelectedOrder(order);
    toggleModal();
  };

  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Historical Orders</CardTitle>
              </CardHeader>
              <CardBody>
                <Row className="mb-3">
                  <Col md="6">
                    <label>Select Date Range:</label>
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={handleDateChange}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                    />
                  </Col>
                </Row>
                {loading ? (
                  <p>Loading historical orders...</p>
                ) : error ? (
                  <p className="text-danger">Error: {error}</p>
                ) : (
                  <Table className="tablesorter" responsive>
                    <thead className="text-primary">
                      <tr>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Customer</th>
                        <th>Bill No.</th>
                        <th>Payment</th>
                        <th>Paid</th>
                        <th>Created At</th>
                        <th>Order Type</th> {/* Added Order Type header */}
                      </tr>
                    </thead>
                    <tbody>
                      {historicalOrders && historicalOrders.length > 0 ? (
                        historicalOrders.map((order) => (
                          <tr key={order.id} onClick={() => handleRowClick(order)} style={{ cursor: 'pointer' }}>
                            <td>{order.id}</td>
                            <td>{order.order_status_name}</td>
                            <td>
                              {order.customer_first_name}{" "}
                              {order.customer_last_name}
                            </td>
                            <td>{order.bill_number}</td>
                            <td>{order.order_payment_type}</td>
                            <td>{order.paid ? "Yes" : "No"}</td>
                            <td>
                              {moment(order.created_at).format(
                                "YYYY-MM-DD HH:mm"
                              )}
                            </td>
                            <td>{order.order_type_name}</td> {/* Display Order Type */}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center"> {/* Updated colspan */}
                            No historical orders found for the selected date range.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Order Details Modal */}
      <Modal isOpen={showModal} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>Order Details - ID: {selectedOrder?.id}</ModalHeader>
        <ModalBody>
          {selectedOrder && (
            <div>
              <p><strong>Status:</strong> {selectedOrder.order_status_name}</p>
              <p><strong>Customer:</strong> {selectedOrder.customer_first_name} {selectedOrder.customer_last_name} ({selectedOrder.customer_phone_number})</p>
              <p><strong>Bill Number:</strong> {selectedOrder.bill_number}</p>
              <p><strong>Payment Type:</strong> {selectedOrder.order_payment_type}</p>
              <p><strong>Paid:</strong> {selectedOrder.paid ? "Yes" : "No"}</p>
              <p><strong>Order Type:</strong> {selectedOrder.order_type_name}</p>
              <p><strong>Created At:</strong> {moment(selectedOrder.created_at).format("YYYY-MM-DD HH:mm:ss")}</p>
              {selectedOrder.special_requests && <p><strong>Special Requests:</strong> {selectedOrder.special_requests}</p>}

              <h5 className="mt-4">Products:</h5>
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Variant</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.order_products && selectedOrder.order_products.map((product, index) => (
                    <tr key={index}>
                      <td>{product.product_name}</td>
                      <td>{product.product_variant_name || 'N/A'}</td>
                      <td>{product.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h5 className="mt-4">Payment Summary:</h5>
              <p><strong>Sub Total:</strong> {selectedOrder.sub_total}</p>
              <p><strong>Gross Amount:</strong> {selectedOrder.gross_amount}</p>
              <p><strong>Product Discount:</strong> {selectedOrder.product_discount}</p>
              <p><strong>Net Total:</strong> {selectedOrder.net_total}</p>
              <p><strong>Tier Discount:</strong> {selectedOrder.tier_discount}</p>
              <p><strong>Taxable Amount:</strong> {selectedOrder.taxable_amount}</p>
              <p><strong>Tax Amount (13% VAT):</strong> {selectedOrder.tax_amount}</p>
              <p><strong>Bill Amount:</strong> {selectedOrder.bill_amount}</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default OrderHistory;
